import {
    getSlidingStoryTitle,
    pickSlidingOriginalImageUrl,
    pickSlidingPlayImageUrl,
} from "../../../../pages/browse-sliding/slidingBrowseUtils";
import SlidingService from "../../../../pages/puzzles/sliding/SlidingService";
import {
    fetchNewestSlidingGroup,
    fetchSlidingById,
    levelToDifficulty,
    pickTemplateFromGroup,
} from "./api";
import { scrambleWithSolvePath } from "./utils";

/**
 * 1080p content capture — same board as `/prototype/mobile/sliding/recording/ending`
 * (screenshot scene 05: Cottage with Lilac, 4×4).
 */
export const CONTENT_RECORDING_STORY_ID = "2dac3787-6312-4c26-b561-3fed6865baa1";
export const CONTENT_RECORDING_SLIDING_ID = "bdf5de3d-cdde-4348-aea6-b3788ea064c5";

const CONTENT_RECORDING_SLIDING_IDS_LEGACY = [
    "bacdf858-f2e9-4bbf-af8a-4a3d1a8cb185",
    "591a5c53-2248-4f92-be91-dc53710c026d",
    "58eb21c1-8605-46be-b448-3434922723c7",
];

export const CONTENT_RECORDING_GRID_SIZE = 4;
export const CONTENT_RECORDING_DIFFICULTY = "hard";

/** Pause after the board is ready before the first auto-solve slide. */
export const CONTENT_RECORDING_INTRO_MS = 1600;
/** Delay between each auto-solve slide. */
export const CONTENT_RECORDING_MOVE_MS = 700;
/**
 * Trailer scramble length — short enough to auto-solve on camera, with a known reverse path
 * (published HARD layouts are too deep for a live solver).
 */
export const CONTENT_RECORDING_SCRAMBLE_MOVES = 28;

/** Query `board` value for the 6×6 hard 1080p content capture. */
export const CONTENT_RECORDING_6X6_HARD_BOARD = "6x6-hard";
export const CONTENT_RECORDING_6X6_HARD_GRID = 6;
export const CONTENT_RECORDING_6X6_HARD_DIFFICULTY = "hard";
/**
 * Longer trailer scramble so a 6×6 reads as hard on camera, with a known reverse path.
 */
export const CONTENT_RECORDING_6X6_HARD_SCRAMBLE_MOVES = 60;
/** Ghost guide plays this many times, spaced through the middle of the resolve. */
export const CONTENT_RECORDING_6X6_HARD_GHOST_GUIDES = 2;
/** How long the "Ghost guide" chip sits on the left menu before the highlights start. */
export const CONTENT_RECORDING_GHOST_CHIP_LEAD_MS = 1400;

/** Deterministic RNG so every recording open uses the same scramble. */
function recordingScrambleRng(seed) {
    let state = seed >>> 0;
    return () => {
        state = (Math.imul(1664525, state) + 1013904223) >>> 0;
        return state / 4294967296;
    };
}

/**
 * @param {URLSearchParams|null|undefined} searchParams
 * @returns {string}
 */
export function contentRecordingBoard(searchParams) {
    return (searchParams?.get("board") || "").trim().toLowerCase();
}

/** 6×6 hard content capture (`?recording=1080p&board=6x6-hard`). */
export function isSliding6x6HardContentRecording(searchParams) {
    return contentRecordingBoard(searchParams) === CONTENT_RECORDING_6X6_HARD_BOARD;
}

/**
 * Content capture play: `?recording=1080p` or a known content `slidingId`.
 * @param {URLSearchParams|null|undefined} searchParams
 * @param {string|null|undefined} slidingId
 */
export function isSlidingContentRecording(searchParams, slidingId) {
    if (searchParams?.get("recording") === "1080p") return true;
    const fromQuery = (searchParams?.get("slidingId") || "").trim().toLowerCase();
    const fromArg = String(slidingId || "").trim().toLowerCase();
    const known = [CONTENT_RECORDING_SLIDING_ID, ...CONTENT_RECORDING_SLIDING_IDS_LEGACY];
    return known.some((id) => fromQuery === id || fromArg === id);
}

function pickTemplateById(group, slidingId) {
    const target = String(slidingId || "").trim().toLowerCase();
    if (!target || !group) return null;
    const templates = Array.isArray(group.templates) ? group.templates : [];
    return templates.find((t) => String(t?.id ?? "").trim().toLowerCase() === target) ?? null;
}

function gridSizeFromTemplate(template, fallback) {
    const n = Number(template?.gridSize ?? template?.grid_size);
    if (Number.isFinite(n) && n >= 3) return n;
    const layout = template?.startingLayout ?? template?.starting_layout;
    if (Array.isArray(layout) && layout.length > 0) {
        const side = Math.round(Math.sqrt(layout.length));
        if (side * side === layout.length) return side;
    }
    return fallback;
}

/**
 * Indexes in the solve path where the ghost guide should play (after that slide lands).
 * Two guides split the path into thirds.
 * @param {number} pathLength
 * @param {number} guideCount
 * @returns {number[]}
 */
export function contentRecordingPauseIndexes(pathLength, guideCount) {
    const length = Number(pathLength) || 0;
    const count = Number(guideCount) || 0;
    if (count <= 0 || length < 3) return [];
    const indexes = [];
    for (let n = 1; n <= count; n += 1) {
        const at = Math.round((length * n) / (count + 1)) - 1;
        const clamped = Math.max(0, Math.min(length - 2, at));
        if (!indexes.includes(clamped)) indexes.push(clamped);
    }
    return indexes;
}

/**
 * Slice indexes tapped to walk `path[fromIndex]` → later layouts (the tiles the ghost guide flashes).
 * @param {number[][]} path
 * @param {number} fromIndex
 * @param {number} count
 * @param {number} gridSize
 * @returns {number[]}
 */
export function solvePathTapSlices(path, fromIndex, count, gridSize) {
    const gs = Number(gridSize);
    const taps = [];
    const start = Math.max(0, Number(fromIndex) || 0);
    const limit = Math.max(0, Number(count) || 0);
    if (!Array.isArray(path) || !Number.isFinite(gs) || gs < 2 || limit <= 0) return taps;
    const empty = gs * gs - 1;
    for (let k = start; k < path.length - 1 && taps.length < limit; k += 1) {
        const before = path[k];
        const after = path[k + 1];
        if (!Array.isArray(before) || !Array.isArray(after)) break;
        const emptyAfter = after.findIndex((v) => Number(v) === empty);
        if (emptyAfter < 0) break;
        taps.push(Number(before[emptyAfter]));
    }
    return taps;
}

function recordingImageUrl(story, picked, detail) {
    return (
        pickSlidingPlayImageUrl(story) ||
        pickSlidingOriginalImageUrl(story) ||
        (typeof picked?.imageUrl === "string" && picked.imageUrl.trim()) ||
        (typeof detail?.imageUrl === "string" && detail.imageUrl.trim()) ||
        null
    );
}

/**
 * 6×6 hard content capture — published hard story image, solvable trailer scramble,
 * ghost guide twice during the resolve.
 */
async function loadSixBySixHardContentRecording() {
    let group = await SlidingService.getStoryGroup(CONTENT_RECORDING_STORY_ID);
    let picked = pickTemplateFromGroup(group, CONTENT_RECORDING_6X6_HARD_GRID, "hard");
    if (!picked) {
        const newest = await fetchNewestSlidingGroup({ excludeCompleted: false });
        const fromNewest = pickTemplateFromGroup(
            newest,
            CONTENT_RECORDING_6X6_HARD_GRID,
            "hard",
        );
        if (fromNewest) {
            group = newest;
            picked = fromNewest;
        }
    }

    const detail = picked?.slidingId ? await fetchSlidingById(picked.slidingId) : null;
    const story = picked?.story || group?.story || detail?.story || null;
    const { layout, solveLayouts } = scrambleWithSolvePath(
        CONTENT_RECORDING_6X6_HARD_GRID,
        CONTENT_RECORDING_6X6_HARD_SCRAMBLE_MOVES,
        recordingScrambleRng(0x6c6e48d2),
    );
    const imageUrl = recordingImageUrl(story, picked, detail);

    return {
        slidingId: picked?.slidingId || CONTENT_RECORDING_SLIDING_ID,
        gridSize: CONTENT_RECORDING_6X6_HARD_GRID,
        difficulty: CONTENT_RECORDING_6X6_HARD_DIFFICULTY,
        layout,
        solveLayouts,
        imageUrl,
        previewImageUrl: pickSlidingOriginalImageUrl(story) || imageUrl,
        storyTitle: picked?.storyTitle || getSlidingStoryTitle(story) || "",
        ghostGuideCount: CONTENT_RECORDING_6X6_HARD_GHOST_GUIDES,
    };
}

/**
 * Exact published board for content recording — story image + that board's scramble.
 * @param {{ board?: string }} [options]
 */
export async function loadContentRecordingPuzzle(options = {}) {
    if (options?.board === CONTENT_RECORDING_6X6_HARD_BOARD) {
        return loadSixBySixHardContentRecording();
    }
    const sid = CONTENT_RECORDING_SLIDING_ID;
    const group = await SlidingService.getStoryGroup(CONTENT_RECORDING_STORY_ID);
    const byId = pickTemplateById(group, sid);
    const gridSize = gridSizeFromTemplate(byId, CONTENT_RECORDING_GRID_SIZE);
    const difficulty =
        levelToDifficulty(byId?.difficultyLevel ?? byId?.difficulty_level) ||
        CONTENT_RECORDING_DIFFICULTY;

    const picked = byId
        ? {
              slidingId: byId.id,
              template: byId,
              story: group?.story ?? null,
              gridSize,
              difficulty,
              imageUrl: pickSlidingPlayImageUrl(group?.story) || byId.imageUrl || null,
              storyTitle: getSlidingStoryTitle(group?.story),
          }
        : pickTemplateFromGroup(group, gridSize, difficulty);

    const detail = picked?.slidingId
        ? await fetchSlidingById(picked.slidingId)
        : await fetchSlidingById(sid);
    const story = picked?.story || group?.story || detail?.story || null;
    const resolvedGrid = gridSizeFromTemplate(picked?.template || detail, gridSize);

    const seed =
        Number.parseInt(String(sid).replace(/\D/g, "").slice(0, 8), 16) || 0x2dac3787;
    const { layout, solveLayouts } = scrambleWithSolvePath(
        resolvedGrid,
        CONTENT_RECORDING_SCRAMBLE_MOVES,
        recordingScrambleRng(seed),
    );

    const imageUrl = recordingImageUrl(story, picked, detail);

    return {
        slidingId: picked?.slidingId || sid,
        gridSize: resolvedGrid,
        difficulty,
        layout,
        solveLayouts,
        imageUrl,
        previewImageUrl: pickSlidingOriginalImageUrl(story) || imageUrl,
        storyTitle: picked?.storyTitle || getSlidingStoryTitle(story) || "",
        ghostGuideCount: 0,
    };
}
